import React, { useState } from 'react';

interface OnboardingIntentModalProps {
  isOpen: boolean;
  onSelectIntent: (intent: 'family' | 'studio' | 'business') => Promise<void>;
  onClose?: () => void;
}

export const OnboardingIntentModal: React.FC<OnboardingIntentModalProps> = ({
  isOpen,
  onSelectIntent,
  onClose,
}) => {
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSelect = async (intent: 'family' | 'studio' | 'business') => {
    setSubmitting(true);
    try {
      await onSelectIntent(intent);
    } catch (e) {
      console.error('Failed to set intent', e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(8px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
      }}
    >
      <div
        className="glass-panel"
        style={{
          background: 'var(--bg-surface, #FFFFFF)',
          color: 'var(--text-primary, #111827)',
          borderRadius: '28px',
          maxWidth: '680px',
          width: '100%',
          padding: '2.5rem',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          position: 'relative',
        }}
      >
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '1.25rem',
              right: '1.25rem',
              background: 'transparent',
              border: 'none',
              fontSize: '1.25rem',
              color: 'var(--text-secondary, #9CA3AF)',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        )}

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #7C3AED, #4F46E5)',
              color: 'white',
              fontSize: '1.8rem',
              marginBottom: '1rem',
              boxShadow: '0 10px 20px rgba(124, 58, 237, 0.3)',
            }}
          >
            ✦
          </div>
          <h2 style={{ fontSize: '1.85rem', fontWeight: 800, margin: '0 0 0.5rem 0', letterSpacing: '-0.02em' }}>
            Welcome to FileSharez!
          </h2>
          <p style={{ color: 'var(--text-secondary, #6B7280)', fontSize: '1.05rem', margin: 0, lineHeight: 1.5 }}>
            Help us personalize your workspace. How do you plan to use FileSharez?
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {/* Option 1: Family & Personal */}
          <button
            onClick={() => handleSelect('family')}
            disabled={submitting}
            style={{
              background: 'var(--bg-subtle, #F9FAFB)',
              border: '2px solid var(--border-color, #E5E7EB)',
              borderRadius: '20px',
              padding: '1.5rem 1rem',
              textAlign: 'center',
              cursor: submitting ? 'wait' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.75rem',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#7C3AED';
              e.currentTarget.style.transform = 'translateY(-3px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color, #E5E7EB)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: '2.4rem' }}>🏠</div>
            <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>Personal & Family</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #6B7280)', lineHeight: 1.4 }}>
              Camera roll backup, private vaults, memory strips, and smart AI burst culling.
            </div>
          </button>

          {/* Option 2: Photographer & Studio */}
          <button
            onClick={() => handleSelect('studio')}
            disabled={submitting}
            style={{
              background: 'var(--bg-subtle, #F9FAFB)',
              border: '2px solid var(--border-color, #E5E7EB)',
              borderRadius: '20px',
              padding: '1.5rem 1rem',
              textAlign: 'center',
              cursor: submitting ? 'wait' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.75rem',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#F59E0B';
              e.currentTarget.style.transform = 'translateY(-3px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color, #E5E7EB)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: '2.4rem' }}>📸</div>
            <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>Photographer / Studio</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #6B7280)', lineHeight: 1.4 }}>
              Client proofing galleries, 1-click approvals, photo comments, and uncompressed delivery.
            </div>
          </button>

          {/* Option 3: Business & Team */}
          <button
            onClick={() => handleSelect('business')}
            disabled={submitting}
            style={{
              background: 'var(--bg-subtle, #F9FAFB)',
              border: '2px solid var(--border-color, #E5E7EB)',
              borderRadius: '20px',
              padding: '1.5rem 1rem',
              textAlign: 'center',
              cursor: submitting ? 'wait' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.75rem',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#0284C7';
              e.currentTarget.style.transform = 'translateY(-3px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color, #E5E7EB)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: '2.4rem' }}>💼</div>
            <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>Business & Team</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #6B7280)', lineHeight: 1.4 }}>
              Brand asset library, team role permissions, guest upload dropboxes, and 4K video.
            </div>
          </button>
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => handleSelect('family')}
            disabled={submitting}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary, #9CA3AF)',
              fontSize: '0.85rem',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};
